/**
 * Meal Track Base MUI Theme Configurations
 *
 * This file contains the base Material-UI theme configurations established by Meal Track for the application.
 *
 * Note: Client-specific configurations will override any settings defined in this file.
 * Modify these configurations as needed to customize the Material-UI theme for the Meal Track project.
 */
import { alpha, createTheme, useTheme } from "@mui/material";
import { darken, getContrastRatio } from "@mui/material/styles";
import cloneDeep from "lodash/cloneDeep"; // Add this import at the top
const DEFAULT_TYPOGRAPHY = {
    typography: {
        htmlFontSize: 16,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontFamily: '"Suez One", serif',
            fontWeight: 300,
            fontSize: "2rem",
            lineHeight: 1.167,
            letterSpacing: "-0.01562em",
        },
        h2: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "1.625rem",
            lineHeight: 1.2,
            letterSpacing: "-0.00833em",
        },
        h3: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            fontSize: "1.25rem",
            lineHeight: 1.2,
            letterSpacing: "-0.00833em",
        },
        h4: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            fontSize: "1rem",
            lineHeight: 1.167,
            letterSpacing: "0em",
        },
        h5: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: 1.334,
            letterSpacing: "0em",
        },
        h6: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: ".75rem",
            lineHeight: 1.235,
            letterSpacing: "0.00735em",
        },
        h7: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            fontSize: ".75rem",
            lineHeight: 1.334,
            letterSpacing: "0em",
        },
        subtitle1: {
            //do we need the subtitles? is this the default? perhaps they should be 0.875rem too
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: 1.75,
            letterSpacing: "0.00938em",
        },
        subtitle2: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: 1.57,
            letterSpacing: "0.00714em",
        },
        body1: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: 1.5,
            letterSpacing: "0.00938em",
        },
        body2: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: ".875rem",
            lineHeight: 1.43,
            letterSpacing: "0.01071em",
        },
        body3: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: ".8125rem",
            lineHeight: 1.66,
            letterSpacing: "0.03333em",
        },
        button: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            fontSize: "0.875rem",
            lineHeight: 1.75,
            letterSpacing: "0.02857em",
            textTransform: "none",
        },
        caption: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "0.75rem",
            lineHeight: 1.66,
            letterSpacing: "0.03333em",
        },
        overline: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
            fontSize: "0.75rem",
            lineHeight: 2.66,
            letterSpacing: "0.08333em",
            textTransform: "none",
        },
    },
};
export const useMealTrackThemeConfigurations = () => {
    // We start with the default MUI theme values. The final theme will be a combination of these values and the client-specific values.
    const DEFAULT_FONTS = [
        /**
         * FONT SIZE QUICK REFERENCE
         * 100        (thin)
         * 100italic  (thin)
         * 300        (light)
         * 300italic  (light)
         * 400        (regular)
         * 400italic  (regular)
         * 500        (medium)
         * 500italic  (medium)
         * 700        (bold)
         * 700italic  (bold)
         * 900        (black)
         * 900italic  (black)
         *
         * weights: ["100", "100italic", "300", "300italic", "400", "400italic", "500", "500italic", "700", "700italic", "900", "900italic"],
         */
        {
            font: "Roboto",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Suez One",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Open Sans",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Lato",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
    ];
    const CUSTOM_BREAKPOINTS = {
        //? ===== USING BREAKPOINTS =====
        //? useMediaQuery takes a key (breakpoint) and uses the following Range Types: up, down, only, not, between.
        //? up: Matches screen widths greater than or equal to the screen size defined by the breakpoint key (inclusive).
        //* theme.breakpoints.up(key) => media query
        //? down: Matches screen widths less than the screen size defined by the breakpoint key (exclusive).
        //* theme.breakpoints.down(key) => media query
        //? only: Matches screen widths starting from the screen size defined by the breakpoint key (inclusive)
        //? and stopping at the screen size defined by the next breakpoint key (exclusive).
        //* theme.breakpoints.only(key) => media query
        //? not: Matches screen widths outside the range of the specified breakpoint,
        //? i.e., below the start of the given breakpoint or above the end of the next one.
        //* theme.breakpoints.not(key) => media query
        //? between: Matches screen widths greater than or equal to the screen size defined by the first breakpoint key (inclusive)
        //? and less than the screen size defined by the second breakpoint key (exclusive).
        //* theme.breakpoints.between(start, end) => media query
        //? When used in the sx prop, the breakpoint range is the same as that of the "up" helper.
        //? E.g., sx breakpoints match screen widths greater than or equal to the screen size defined by the breakpoint key (inclusive).
        //* <Box
        //*   sx={{
        //*     padding: {
        //*       xx: 1,    // padding: 8px for screens 0px and up (custom zero breakpoint)
        //*       xs: 2,    // padding: 16px for screens 320px and up
        //*       sm: 3,    // padding: 24px for screens 600px and up
        //*     },
        //*   }}
        //* >
        breakpoints: {
            values: {
                xx: 0,
                xs: 320,
                sm: 600,
                md: 960,
                container: 1140,
                lg: 1280,
                xl: 1920,
            },
        },
    };
    const OVERRIDES = {
        components: {
            MuiAccordionSummary: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundColor: alpha(theme.palette.primary.light, 0.3),
                    }),
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: ({ theme }) => {
                        var _a, _b, _c, _d;
                        const headerTypography = theme.typography.header;
                        const footerTypography = theme.typography.footer;
                        const headerTextColor = ((_a = theme.palette.header) === null || _a === void 0 ? void 0 : _a.textColor) || theme.palette.common.black;
                        const headerBgColor = ((_b = theme.palette.header) === null || _b === void 0 ? void 0 : _b.backgroundColor) || theme.palette.common.white;
                        const footerBgColor = ((_c = theme.palette.footer) === null || _c === void 0 ? void 0 : _c.backgroundColor) || theme.palette.primary.main;
                        const footerTextColor = getAccessibleTextColor(theme, ((_d = theme.palette.footer) === null || _d === void 0 ? void 0 : _d.textColor) || theme.palette.common.black, footerBgColor);
                        const headerNavTextColor = getAccessibleTextColor(theme, headerTextColor, headerBgColor);
                        return {
                            // Not a fan of using !important, but it's necessary here to override the padding added when the appbar is sticky and a modal/popup/popper/menu is open.
                            // This is a MUI regression issue and has to do with padding being added to accommodate the scrollbar.
                            paddingRight: "0 !important",
                            "&.nav-appbar-overrides": {
                                // Default styles for navbar.
                                position: "static",
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: headerNavTextColor,
                                // This is setup for client configuration
                                backgroundColor: headerBgColor,
                                ".navWrapper": getNavWrapperStyles(theme, headerTypography, headerNavTextColor, headerBgColor),
                            },
                            "&.footer-appbar-overrides": {
                                // Default styles for footer.
                                top: "auto",
                                bottom: 0,
                                position: "static",
                                // This is setup for client configuration
                                backgroundColor: footerBgColor,
                                ".MuiTypography-root": {
                                    color: footerTextColor,
                                    fontFamily: footerTypography === null || footerTypography === void 0 ? void 0 : footerTypography.fontFamily,
                                    fontWeight: footerTypography === null || footerTypography === void 0 ? void 0 : footerTypography.fontWeight,
                                    fontSize: footerTypography === null || footerTypography === void 0 ? void 0 : footerTypography.fontSize,
                                    textTransform: footerTypography === null || footerTypography === void 0 ? void 0 : footerTypography.textTransform,
                                },
                            },
                        };
                    },
                },
            },
            MuiButton: {
                variants: [
                    {
                        props: { variant: "contained" },
                        style: ({ theme }) => {
                            var _a, _b;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            return getBaseContainedButtonStyles(theme, rootStyles);
                        },
                    },
                    {
                        props: { variant: "contained", color: "primary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const primaryStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedPrimary;
                            return getBaseContainedButtonStyles(theme, rootStyles, {
                                backgroundColor: (primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.backgroundColor) || theme.palette.primary.main,
                                color: (primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.textColor) || theme.palette.primary.contrastText,
                                borderColor: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderColor,
                                borderWidth: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderWidth,
                                borderStyle: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderStyle,
                                borderRadius: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderRadius,
                                hoverBackgroundColor: getHoverColor(primaryStyles, "backgroundColor", theme.palette.primary.main),
                                hoverColor: (_f = (_e = primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles["&:hover"]) === null || _e === void 0 ? void 0 : _e.textColor) !== null && _f !== void 0 ? _f : primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.textColor,
                                hoverBorderColor: getHoverColor(primaryStyles, "borderColor", theme.palette.primary.main),
                                hoverBorderRadius: (_g = primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles["&:hover"]) === null || _g === void 0 ? void 0 : _g.borderRadius,
                            });
                        },
                    },
                    {
                        props: { variant: "contained", color: "secondary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const secondaryStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedSecondary;
                            return getBaseContainedButtonStyles(theme, rootStyles, {
                                backgroundColor: (_e = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.backgroundColor) !== null && _e !== void 0 ? _e : theme.palette.secondary.main,
                                color: (_f = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.textColor) !== null && _f !== void 0 ? _f : theme.palette.secondary.contrastText,
                                borderColor: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderColor,
                                borderWidth: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderWidth,
                                borderStyle: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderStyle,
                                borderRadius: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderRadius,
                                hoverBackgroundColor: getHoverColor(secondaryStyles, "backgroundColor", theme.palette.secondary.main),
                                hoverColor: (_h = (_g = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles["&:hover"]) === null || _g === void 0 ? void 0 : _g.textColor) !== null && _h !== void 0 ? _h : secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.textColor,
                                hoverBorderColor: getHoverColor(secondaryStyles, "borderColor", theme.palette.secondary.main),
                                hoverBorderRadius: (_j = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles["&:hover"]) === null || _j === void 0 ? void 0 : _j.borderRadius,
                            });
                        },
                    },
                    {
                        props: { variant: "text" },
                        style: ({ theme }) => {
                            var _a, _b;
                            return ({
                                border: "0",
                                borderRadius: ((_b = (_a = theme.components.MuiButton.styleOverrides) === null || _a === void 0 ? void 0 : _a.root) === null || _b === void 0 ? void 0 : _b.borderRadius) || theme.shape.borderRadius,
                            });
                        },
                    },
                    {
                        props: { variant: "text", color: "primary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const overrideStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedPrimary;
                            const bg = (overrideStyles === null || overrideStyles === void 0 ? void 0 : overrideStyles.backgroundColor) || theme.palette.secondary.main;
                            return {
                                borderRadius: (overrideStyles === null || overrideStyles === void 0 ? void 0 : overrideStyles.borderRadius) ||
                                    (rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderRadius) ||
                                    ((_g = (_f = (_e = theme.components.MuiButton) === null || _e === void 0 ? void 0 : _e.styleOverrides) === null || _f === void 0 ? void 0 : _f.root) === null || _g === void 0 ? void 0 : _g.borderRadius) ||
                                    theme.shape.borderRadius,
                                "&:hover": {
                                    backgroundColor: alpha(bg, theme.palette.action.hoverOpacity),
                                },
                            };
                        },
                    },
                    {
                        props: { variant: "text", color: "secondary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const overrideStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedPrimary;
                            const bg = (overrideStyles === null || overrideStyles === void 0 ? void 0 : overrideStyles.backgroundColor) || theme.palette.secondary.main;
                            return {
                                borderRadius: (overrideStyles === null || overrideStyles === void 0 ? void 0 : overrideStyles.borderRadius) ||
                                    (rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderRadius) ||
                                    ((_g = (_f = (_e = theme.components.MuiButton) === null || _e === void 0 ? void 0 : _e.styleOverrides) === null || _f === void 0 ? void 0 : _f.root) === null || _g === void 0 ? void 0 : _g.borderRadius) ||
                                    theme.shape.borderRadius,
                                "&:hover": {
                                    backgroundColor: alpha(bg, theme.palette.action.hoverOpacity),
                                },
                            };
                        },
                    },
                    {
                        props: { variant: "outlined" },
                        style: ({ theme }) => {
                            var _a, _b;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            return getBaseOutlinedButtonStyles(theme, rootStyles);
                        },
                    },
                    {
                        props: { variant: "outlined", color: "primary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const primaryStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedPrimary;
                            const borderColor = (primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.backgroundColor) || (rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.backgroundColor) || theme.palette.primary.main;
                            const borderWidth = Number(primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderWidth) > 0 ? Number(primaryStyles.borderWidth) : 1;
                            return getBaseOutlinedButtonStyles(theme, rootStyles, {
                                borderColor,
                                borderWidth,
                                borderStyle: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderStyle,
                                borderRadius: primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles.borderRadius,
                                hoverColor: (_e = primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles["&:hover"]) === null || _e === void 0 ? void 0 : _e.textColor,
                                hoverBorderColor: (_f = primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles["&:hover"]) === null || _f === void 0 ? void 0 : _f.borderColor,
                                hoverBorderRadius: (_g = primaryStyles === null || primaryStyles === void 0 ? void 0 : primaryStyles["&:hover"]) === null || _g === void 0 ? void 0 : _g.borderRadius,
                            });
                        },
                    },
                    {
                        props: { variant: "outlined", color: "secondary" },
                        style: ({ theme }) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            const rootStyles = (_b = (_a = theme.components.MuiButton) === null || _a === void 0 ? void 0 : _a.styleOverrides) === null || _b === void 0 ? void 0 : _b.root;
                            const secondaryStyles = (_d = (_c = theme.components.MuiButton) === null || _c === void 0 ? void 0 : _c.styleOverrides) === null || _d === void 0 ? void 0 : _d.containedSecondary;
                            const borderColor = (secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderColor) || (rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderColor) || theme.palette.secondary.main;
                            const borderWidth = Number(secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderWidth) > 0 ? Number(secondaryStyles.borderWidth) : 1;
                            return getBaseOutlinedButtonStyles(theme, rootStyles, {
                                borderColor,
                                borderWidth,
                                borderStyle: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderStyle,
                                borderRadius: secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles.borderRadius,
                                hoverColor: (_e = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles["&:hover"]) === null || _e === void 0 ? void 0 : _e.textColor,
                                hoverBorderColor: (_f = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles["&:hover"]) === null || _f === void 0 ? void 0 : _f.borderColor,
                                hoverBorderRadius: (_g = secondaryStyles === null || secondaryStyles === void 0 ? void 0 : secondaryStyles["&:hover"]) === null || _g === void 0 ? void 0 : _g.borderRadius,
                            });
                        },
                    },
                ],
            },
            MuiChip: {
                variants: [
                    {
                        props: {
                            variant: "filledSquared",
                        },
                        style: {
                            borderRadius: "4px",
                        },
                    },
                    {
                        props: {
                            variant: "outlinedSquared",
                        },
                        style: {
                            borderRadius: "4px",
                        },
                    },
                ],
                variantMapping: {
                    filledSquared: "filled",
                    outlinedSquared: "outlined", // Render outlinedSquared variant as a outlined variant.
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    root: ({ theme }) => {
                        var _a, _b;
                        const headerTypography = theme.typography.header;
                        const headerTextColor = ((_a = theme.palette.header) === null || _a === void 0 ? void 0 : _a.textColor) || theme.palette.common.black;
                        const headerBgColor = ((_b = theme.palette.header) === null || _b === void 0 ? void 0 : _b.backgroundColor) || theme.palette.common.white;
                        return {
                            ".navWrapper": getNavWrapperStyles(theme, headerTypography, headerTextColor, headerBgColor),
                        };
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body1.fontSize,
                        color: theme.palette.common.black,
                        "&.Mui-focused": {
                            // This ensures that the label color is black when focused.
                            color: theme.palette.common.black,
                            fontWeight: 500,
                        },
                    }),
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body1.fontSize,
                        backgroundColor: alpha(theme.palette.primary.light, 0.1),
                        // If we are using an alpha on the primary or secondary light color, the contrast text should be black.
                        color: theme.palette.common.black,
                    }),
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        "&.topbar-overrides": {
                            minHeight: 0,
                            "@media screen and (min-width: 0px)": {
                                minHeight: 0,
                            },
                        },
                    }),
                },
            },
            MuiTypography: {
                variants: [
                    {
                        props: {
                            variant: "h7",
                        },
                        style: {},
                    },
                    {
                        props: {
                            variant: "body3",
                        },
                        style: {},
                    },
                ],
                variantMapping: {
                    h7: "h6",
                    body3: "p", // Render body3 variant as p element
                },
            },
        },
    };
    const MEAL_TRACK_THEME_CONFIGURATIONS = Object.assign(Object.assign(Object.assign({}, CUSTOM_BREAKPOINTS), DEFAULT_TYPOGRAPHY), OVERRIDES);
    const MT_THEME = createTheme(Object.assign({}, MEAL_TRACK_THEME_CONFIGURATIONS));
    return {
        MT_THEME,
        DEFAULT_FONTS,
    };
};
/**
 * Converts the fontSize properties in the clientThemeConfigurations object from pixel units to rem units.
 * This function is a temporary measure to complete the ticket. When further updating the
 * theme in the future, this function should be removed and all font sizes should be
 * converted to rem units when they are set in KMS or when being mapped and sent to
 * the database. However, this would require a significant amount of work and testing
 * that would significantly expand the scope of the ticket.
 * @param clientThemeConfigurations The client-specific theme configurations to convert.
 * @returns The clientThemeConfigurations object with fontSize properties converted to rem units.
 */
export const configureClientTheme = (clientThemeConfigurations) => {
    /**
     * helper to scale and assign font sizes for a set of typography keys.
     * Calculates default sizes/ratios, determines base rem, and sets/creates fontSize.
     * @param keys Typography variant keys to scale (e.g., headings, body).
     * @param draftTypography The typography object to mutate.
     * @param defaultTypography The default typography object for reference ratios.
     * @param baseRem Optional: override base rem value (otherwise uses first key's value).
     */
    function scaleTypographySet(keys, draftTypography, defaultTypography, baseRem) {
        var _a;
        // Calculate default sizes and ratios
        const defaultSizes = keys.map((k) => { var _a, _b; return parseFloat(((_b = (_a = defaultTypography[k]) === null || _a === void 0 ? void 0 : _a.fontSize) === null || _b === void 0 ? void 0 : _b.replace("rem", "")) || "1"); });
        const base = defaultSizes[0];
        const ratios = defaultSizes.map((size) => size / base);
        // Determine base rem (from draft if present, else from param, else from default)
        let actualBaseRem = baseRem;
        if ((_a = draftTypography[keys[0]]) === null || _a === void 0 ? void 0 : _a.fontSize) {
            actualBaseRem = getBaseRem(draftTypography[keys[0]].fontSize, base);
        }
        else if (typeof baseRem !== "number") {
            actualBaseRem = base;
        }
        // Set or create fontsize values for each key
        keys.forEach((key, idx) => {
            const fontSize = `${actualBaseRem * ratios[idx]}rem`;
            if (draftTypography[key] && typeof draftTypography[key] === "object") {
                draftTypography[key].fontSize = fontSize;
            }
            else {
                draftTypography[key] = { fontSize };
            }
        });
    }
    function scaleTypographyVariants(draftTypography, defaultTypography) {
        var _a, _b;
        const headingKeys = ["h1", "h2", "h3", "h4", "h5", "h6", "h7", "subtitle1", "subtitle2"];
        const bodyKeys = ["body1", "body2", "body3", "caption", "button", "overline"];
        const allKeys = Object.keys(defaultTypography);
        // Headings
        scaleTypographySet(headingKeys, draftTypography, defaultTypography);
        // Body
        scaleTypographySet(bodyKeys, draftTypography, defaultTypography);
        // Catch other variants
        const body1Rem = getBaseRem((_b = (_a = draftTypography.body1) === null || _a === void 0 ? void 0 : _a.fontSize) !== null && _b !== void 0 ? _b : defaultTypography.body1.fontSize, 1);
        allKeys.forEach((key) => {
            if (!headingKeys.includes(key) && !bodyKeys.includes(key)) {
                if (draftTypography[key] && typeof draftTypography[key] === "object") {
                    draftTypography[key].fontSize = `${body1Rem}rem`;
                }
            }
        });
    }
    const { palette: { augmentColor }, } = useTheme();
    // Deep clone to avoid mutating the original input
    const draft = cloneDeep(clientThemeConfigurations);
    // Recursively traverse the object and convert all fontSize properties to rem units.
    const traverse = (obj, path = []) => {
        for (const key in obj) {
            if (typeof obj[key] === "object" && obj[key] !== null) {
                traverse(obj[key], [...path, key]);
            }
            else if (key === "fontSize" &&
                (typeof obj[key] === "string" || typeof obj[key] === "number") &&
                // Do not convert the base fontSize at the root of typography
                !(path.length === 1 && path[0] === "typography")) {
                const pxValue = typeof obj[key] === "number" ? obj[key] : parseInt(obj[key]);
                obj[key] = useTheme().typography.pxToRem(pxValue);
            }
            else if (key === "disableRipple" && typeof obj[key] === "string") {
                // Convert string boolean values to actual booleans
                obj[key] = obj[key] === "true";
            }
            else if (key === "fontWeight" && typeof obj[key] === "string") {
                // Convert Google Fonts variant names to CSS font-weight values
                if (obj[key] === "regular" || obj[key] === "italic") {
                    obj[key] = "400";
                }
                else if (obj[key].includes("italic")) {
                    obj[key] = obj[key].replace("italic", "");
                }
            }
        }
    };
    traverse(draft);
    // Augment palette colors
    const paletteKeys = ["primary", "secondary", "error", "warning", "success", "info"];
    paletteKeys.forEach((key) => {
        if (draft.palette && draft.palette[key]) {
            draft.palette[key] = augmentColor({ color: draft.palette[key] });
        }
    });
    /**
     * Converts a font size value to rem units as a number.
     *
     * Accepts a value in px (number or string), rem (string), or returns the fallback if not recognized.
     *
     * @param size - The font size to convert. Can be a number (px), a string ending in 'px' or 'rem'.
     * @param fallback - The fallback value to use if size is not recognized.
     * @returns The font size in rem units as a number.
     *
     * @example
     * getBaseRem(32, 1) // 2
     * getBaseRem('24px', 1) // 1.5
     * getBaseRem('1.25rem', 1) // 1.25
     * getBaseRem(undefined, 1) // 1
     */
    const getBaseRem = (size, fallback) => {
        if (typeof size === "number")
            return size / 16;
        if (typeof size === "string" && size.endsWith("px"))
            return parseFloat(size) / 16;
        if (typeof size === "string" && size.endsWith("rem"))
            return parseFloat(size);
        return fallback;
    };
    if (!draft.typography)
        draft.typography = {};
    scaleTypographyVariants(draft.typography, DEFAULT_TYPOGRAPHY.typography);
    return draft;
};
/**
 * Returns the best text color for a given background, using a preferred color if it passes contrast.
 * @param theme The MUI theme object.
 * @param preferredColor The text color you want to use if possible.
 * @param backgroundColor The background color to check against.
 * @returns The preferredColor if it passes contrast, otherwise the theme's contrast text color.
 */
export function getAccessibleTextColor(theme, preferredColor, backgroundColor) {
    const contrastText = theme.palette.getContrastText(backgroundColor);
    const ratio = getContrastRatio(preferredColor, backgroundColor);
    const passes = ratio >= theme.palette.contrastThreshold;
    return passes ? preferredColor : contrastText;
}
/**
 * Returns the hover color for a given property (backgroundColor, borderColor, etc).
 * - If an explicit hover value is defined, use it.
 * - If a base value is defined, use a darkened version of it.
 * - Otherwise, use a darkened fallback.
 */
export function getHoverColor(styles, property, fallback) {
    var _a;
    const hoverValue = (_a = styles === null || styles === void 0 ? void 0 : styles["&:hover"]) === null || _a === void 0 ? void 0 : _a[property];
    const baseValue = styles === null || styles === void 0 ? void 0 : styles[property];
    if (hoverValue)
        return hoverValue;
    if (baseValue)
        return darken(baseValue, 0.2);
    return darken(fallback, 0.2);
}
/**
 * Creates a reusable Material-UI transition string for use in style overrides or components.
 *
 * This utility wraps the MUI theme's `transitions.create` method, allowing you to easily specify
 * which CSS properties should transition, as well as optional duration and easing values.
 *
 * @param theme - The MUI Theme object, used to access transition defaults.
 * @param properties - An array of CSS property names to include in the transition.
 *                     Defaults to common UI properties: ["border-radius", "background-color", "border-color", "box-shadow", "color"].
 * @param options - Optional object to override the default duration and easing.
 *                  - duration: The transition duration in ms. Defaults to theme.transitions.duration.short.
 *                  - easing: The transition timing function. Defaults to theme.transitions.easing.easeInOut.
 * @returns A CSS transition string suitable for use in style overrides or the `sx` prop.
 *
 * @example
 * // Basic usage in a style override:
 * transition: createMuiTransition(theme)
 *
 * @example
 * // Custom properties and duration:
 * transition: createMuiTransition(theme, ["background-color", "color"], { duration: 300 })
 */ export function createMuiTransition(theme, properties = ["border-radius", "background-color", "border-color", "box-shadow", "color"], options) {
    var _a, _b;
    return theme.transitions.create(properties, {
        duration: (_a = options === null || options === void 0 ? void 0 : options.duration) !== null && _a !== void 0 ? _a : theme.transitions.duration.short,
        easing: (_b = options === null || options === void 0 ? void 0 : options.easing) !== null && _b !== void 0 ? _b : theme.transitions.easing.easeInOut,
    });
}
/**
 * Returns the base outlined button styles, allowing overrides for color, borderWidth, etc.
 * @param theme - The MUI theme object.
 * @param rootStyles - The base styleOverrides from the theme.
 * @param overrides - Optional overrides for color, borderWidth, etc.
 */
function getBaseOutlinedButtonStyles(theme, rootStyles, overrides) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const borderColor = (_b = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.borderColor) !== null && _a !== void 0 ? _a : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderColor) !== null && _b !== void 0 ? _b : theme.palette.primary.main;
    const borderWidth = (_d = (_c = overrides === null || overrides === void 0 ? void 0 : overrides.borderWidth) !== null && _c !== void 0 ? _c : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderWidth) !== null && _d !== void 0 ? _d : 1;
    const borderStyle = (_f = (_e = overrides === null || overrides === void 0 ? void 0 : overrides.borderStyle) !== null && _e !== void 0 ? _e : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderStyle) !== null && _f !== void 0 ? _f : "solid";
    const borderRadius = (_h = (_g = overrides === null || overrides === void 0 ? void 0 : overrides.borderRadius) !== null && _g !== void 0 ? _g : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderRadius) !== null && _h !== void 0 ? _h : theme.shape.borderRadius;
    const color = (_j = overrides === null || overrides === void 0 ? void 0 : overrides.color) !== null && _j !== void 0 ? _j : borderColor;
    const hoverColor = getAccessibleTextColor(theme, (overrides === null || overrides === void 0 ? void 0 : overrides.hoverColor) || darken(borderColor, 0.2), theme.palette.background.paper);
    const hoverBorderColor = (_k = overrides === null || overrides === void 0 ? void 0 : overrides.hoverBorderColor) !== null && _k !== void 0 ? _k : darken(borderColor, 0.2);
    const hoverBorderRadius = (_l = overrides === null || overrides === void 0 ? void 0 : overrides.hoverBorderRadius) !== null && _l !== void 0 ? _l : borderRadius;
    return {
        color,
        borderColor,
        borderWidth,
        borderStyle,
        borderRadius,
        transition: createMuiTransition(theme),
        "&:hover": {
            borderWidth,
            borderColor: hoverBorderColor,
            color: hoverColor,
            borderRadius: hoverBorderRadius,
        },
        "&.Mui-disabled": {
            borderWidth,
        },
        "&.MuiLoadingButton-loading": {
            borderWidth,
        },
    };
}
/**
 * Returns the base contained button styles, allowing overrides for color, background, border, etc.
 * @param theme - The MUI theme object.
 * @param rootStyles - The root styleOverrides from the theme.
 * @param overrides - Optional overrides for backgroundColor, color, border, etc.
 */
function getBaseContainedButtonStyles(theme, rootStyles, overrides) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    const backgroundColor = (_b = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.backgroundColor) !== null && _a !== void 0 ? _a : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.backgroundColor) !== null && _b !== void 0 ? _b : theme.palette.primary.main;
    const borderColor = (_c = overrides === null || overrides === void 0 ? void 0 : overrides.borderColor) !== null && _c !== void 0 ? _c : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderColor;
    const borderWidth = (_d = overrides === null || overrides === void 0 ? void 0 : overrides.borderWidth) !== null && _d !== void 0 ? _d : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderWidth;
    const borderStyle = (_f = (_e = overrides === null || overrides === void 0 ? void 0 : overrides.borderStyle) !== null && _e !== void 0 ? _e : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderStyle) !== null && _f !== void 0 ? _f : "solid";
    const borderRadius = (_h = (_g = overrides === null || overrides === void 0 ? void 0 : overrides.borderRadius) !== null && _g !== void 0 ? _g : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.borderRadius) !== null && _h !== void 0 ? _h : theme.shape.borderRadius;
    const hoverBackgroundColor = (_l = (_j = overrides === null || overrides === void 0 ? void 0 : overrides.hoverBackgroundColor) !== null && _j !== void 0 ? _j : (_k = rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles["&:hover"]) === null || _k === void 0 ? void 0 : _k.backgroundColor) !== null && _l !== void 0 ? _l : darken(backgroundColor, 0.2);
    const color = getAccessibleTextColor(theme, (_o = (_m = overrides === null || overrides === void 0 ? void 0 : overrides.color) !== null && _m !== void 0 ? _m : rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles.textColor) !== null && _o !== void 0 ? _o : theme.palette.primary.contrastText, backgroundColor);
    const hoverColor = (_r = (_p = overrides === null || overrides === void 0 ? void 0 : overrides.hoverColor) !== null && _p !== void 0 ? _p : (_q = rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles["&:hover"]) === null || _q === void 0 ? void 0 : _q.textColor) !== null && _r !== void 0 ? _r : color;
    const hoverColorToUse = getAccessibleTextColor(theme, hoverColor, hoverBackgroundColor);
    const hoverBorderColor = (_u = (_s = overrides === null || overrides === void 0 ? void 0 : overrides.hoverBorderColor) !== null && _s !== void 0 ? _s : (_t = rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles["&:hover"]) === null || _t === void 0 ? void 0 : _t.borderColor) !== null && _u !== void 0 ? _u : (borderColor ? darken(borderColor, 0.2) : undefined);
    const hoverBorderRadius = (_x = (_v = overrides === null || overrides === void 0 ? void 0 : overrides.hoverBorderRadius) !== null && _v !== void 0 ? _v : (_w = rootStyles === null || rootStyles === void 0 ? void 0 : rootStyles["&:hover"]) === null || _w === void 0 ? void 0 : _w.borderRadius) !== null && _x !== void 0 ? _x : borderRadius;
    return {
        backgroundColor,
        color,
        borderColor,
        borderWidth,
        borderStyle,
        borderRadius,
        transition: createMuiTransition(theme),
        "&:hover": {
            backgroundColor: hoverBackgroundColor,
            color: hoverColorToUse,
            borderColor: hoverBorderColor,
            borderRadius: hoverBorderRadius,
        },
        "&.Mui-disabled": {
            borderColor: borderColor ? alpha(borderColor, theme.palette.action.disabledOpacity) : undefined,
        },
    };
}
function getNavWrapperStyles(theme, headerTypography, headerNavTextColor, headerBgColor) {
    var _a, _b, _c, _d;
    const textColorToUse = getAccessibleTextColor(theme, headerNavTextColor !== null && headerNavTextColor !== void 0 ? headerNavTextColor : theme.palette.common.black, headerBgColor);
    return {
        ".MuiTypography-root": {
            fontFamily: (_a = headerTypography === null || headerTypography === void 0 ? void 0 : headerTypography.navText) === null || _a === void 0 ? void 0 : _a.fontFamily,
            fontWeight: (_b = headerTypography === null || headerTypography === void 0 ? void 0 : headerTypography.navText) === null || _b === void 0 ? void 0 : _b.fontWeight,
            textTransform: (_c = headerTypography === null || headerTypography === void 0 ? void 0 : headerTypography.navText) === null || _c === void 0 ? void 0 : _c.textTransform,
            fontSize: (_d = headerTypography === null || headerTypography === void 0 ? void 0 : headerTypography.navText) === null || _d === void 0 ? void 0 : _d.fontSize,
            textDecorationColor: textColorToUse,
        },
        ".MuiSvgIcon-root": {
            width: "1rem",
            height: "1rem",
        },
    };
}
